import { create } from "zustand";

import { User, anonymousUser } from "../../types";
import { Farm, undefinedFarm } from "../../types";
import { useKeycloak } from "@react-keycloak/web";
import { jwtDecode } from 'jwt-decode';

/**
 * Session store is a zustand store that holds the current user and the current farm.
 * 
 * The current user is the user that is currently logged in.  The current farm is the farm
 * that the user is currently working with.
 * 
 * The session store also holds the state of the sidebar.  The sidebar is the navigation bar.
 */
type SessionStore = {

    // User-related state
    //
    currentUser: User;
    setCurrentUser: (user: User) => void;
    getIdentity:(keycloak: any) => Promise<User>;
    logOut: () => void;

    // Farm-related state
    //
    currentFarm: Farm;
    setCurrentFarm: (farm: Farm) => void;

    // Sidebar state
    //
    sideBarOpen: boolean;
    toggleSidebar:() => void;

    productCount: number;
    setProductCount: (count: number) => void;

    selectedYear: number;
    setSelectedYear: (year: number) => void;

    lastTractorID: string;
    setLastTractorID: (tractorID: string) => void;

    lastSprayerID: string;
    setLastSprayerID: (sprayerID: string) => void;

    lastDriverID: string;
    setLastDriverID: (driverID: string) => void;

    lastPreparerID: string;
    setLastPreparerID: (preparerID: string) => void;
};

  
export const useSessionStore = create<SessionStore>(
    
    (set) => ({    

    currentUser: anonymousUser,
    setCurrentUser: (user) => set({currentUser: user}),
    getIdentity: (keycloak) => {

            if (keycloak.authenticated && keycloak.token) {
                try {
                    const decoded : any = jwtDecode(keycloak.token);
                    // TODO const id = decoded.sub
                    const fullName = decoded.name       

                    const user : User = {
                        fullName: fullName,
                        avatar: '../assets/img/avatars/avatar-5.jpg'  // TODO
                    }
                    return Promise.resolve(user)
                } catch (error) {
                    console.error('Failed to decode token:', error);
                    return Promise.reject("Failed to decode user token");
                }
            }
            return Promise.reject("User not authenticated");
    },

    // Log out the current user
    //
    logOut: () => set((state) => {
        const { keycloak } = useKeycloak();
        keycloak.logout({
            redirectUri: window.location.origin + '/'
        });
        return {...state, currentUser: anonymousUser}
    }),

    currentFarm: undefinedFarm,
    setCurrentFarm: (farm: Farm) => set((state) => {
        if (farm.farmID == "null") {
            return {...state, currentFarm: undefinedFarm}
        }
        return {...state, currentFarm: farm}
    }),

    sideBarOpen: true,
    toggleSidebar: () => set((state) => ({...state, sideBarOpen: !state.sideBarOpen})),

    productCount: 0,
    setProductCount: (count: number) => set((state) => ({productCount: count})),

    selectedYear: Number(localStorage.getItem("selectedYear")) || (new Date()).getFullYear(),
    setSelectedYear: (year: number) => set((state) => {
        localStorage.setItem("selectedYear", year+'');
        return {...state, selectedYear: year}
    }),

    lastTractorID: localStorage.getItem("lastTractorID") || '',
    setLastTractorID: (tractorID: string) => set((state) => {
        localStorage.setItem("lastTractorID", tractorID || '');
        return {...state, lastTractorID: tractorID}
    }),

    lastSprayerID: localStorage.getItem("lastSprayerID") || '',
    setLastSprayerID: (sprayerID: string) => set((state) => {
        localStorage.setItem("lastSprayerID", sprayerID || '');
        return {...state, lastSprayerID: sprayerID}
    }),

    lastDriverID: localStorage.getItem("lastDriverID") || '',
    setLastDriverID: (driverID: string) => set((state) => {
        localStorage.setItem("lastDriverID", driverID || '');
        return {...state, lastDriverID: driverID}
    }),

    lastPreparerID: localStorage.getItem("lastPreparerID") || '',
    setLastPreparerID: (preparerID: string) => set((state) => {
        localStorage.setItem("lastPreparerID", preparerID || '');
        return {...state, lastPreparerID: preparerID}
    })
}));